/*
 * ZELZIS random product ordering
 * - Randomizes the main collection grid on each fresh visit / refresh.
 * - Randomizes eligible non-carousel product grids inside collection-based product blocks.
 * - Leaves promotional banner slots, explicit collection sorting, and initialized sliders alone.
 * - Waits until the theme's own DOM/lazy-load/slider initialization has run before touching grids.
 */
(function () {
  'use strict';

  if (window.ZelzisRandomProducts) return;

  var COLLECTION_GRID_SELECTOR = '#main-collection-product-grid.productListing';
  var PRODUCT_BLOCK_GRID_SELECTOR = '[data-product-block] .products-grid, [data-product-block] .products-flex, [data-product-tab-block] .products-grid, [data-product-tab-block] .products-flex, [data-product-block] ul.productListing, [data-product-tab-block] ul.productListing';
  var cardIds = new WeakMap();
  var nextCardId = 1;
  var gridSignatures = new WeakMap();
  var scanTimer = null;
  var pageLoaded = document.readyState === 'complete';

  function getCardId(card) {
    if (!cardIds.has(card)) cardIds.set(card, nextCardId++);
    return cardIds.get(card);
  }

  function getCards(grid) {
    return Array.prototype.filter.call(grid.children, function (child) {
      return child.classList &&
        child.classList.contains('product') &&
        !child.classList.contains('banner') &&
        !child.hasAttribute('data-product-banner');
    });
  }

  function getSignature(cards) {
    return cards.map(getCardId).sort(function (a, b) { return a - b; }).join(',');
  }

  function shuffleArray(items) {
    var result = items.slice();
    for (var i = result.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var temp = result[i];
      result[i] = result[j];
      result[j] = temp;
    }

    // On a randomization event, avoid accidentally leaving the complete order unchanged.
    if (result.length > 1 && result.every(function (item, index) { return item === items[index]; })) {
      var first = result[0];
      result[0] = result[1];
      result[1] = first;
    }
    return result;
  }

  function isCollectionGrid(grid) {
    return document.body.classList.contains('template-collection') &&
      grid.matches(COLLECTION_GRID_SELECTOR);
  }

  function isEligibleProductBlockGrid(grid) {
    if (!pageLoaded) return false;
    if (!grid.matches('.products-grid, .products-flex, ul.productListing')) return false;

    var block = grid.closest('[data-product-block], [data-product-tab-block]');
    if (!block) return false;

    // Do not reorder slider/carousel markup. The theme's slider initializer owns that DOM.
    if (block.getAttribute('data-layout') === 'slider' ||
        grid.closest('.products-carousel, .slick-slider, .slick-initialized') ||
        grid.classList.contains('slick-initialized') ||
        grid.querySelector('.slick-track')) {
      return false;
    }

    return true;
  }

  function hasExplicitCollectionSort(grid) {
    if (!isCollectionGrid(grid)) return false;
    try {
      return new URL(window.location.href).searchParams.has('sort_by');
    } catch (error) {
      return false;
    }
  }

  function shuffleGrid(grid, force) {
    if (!grid || !grid.isConnected) return false;

    var collectionGrid = isCollectionGrid(grid);
    if (!collectionGrid && !isEligibleProductBlockGrid(grid)) return false;
    if (collectionGrid && hasExplicitCollectionSort(grid)) return false;

    // Never move cards inside an initialized slider/carousel.
    if (grid.classList.contains('slick-initialized') ||
        grid.closest('.slick-slider, .slick-initialized') ||
        grid.querySelector('.slick-track')) return false;

    var cards = getCards(grid);
    if (cards.length < 2) {
      gridSignatures.set(grid, getSignature(cards));
      return false;
    }

    var signature = getSignature(cards);
    if (!force && gridSignatures.get(grid) === signature) return false;

    var shuffled = shuffleArray(cards);
    var slots = cards.map(function () {
      return document.createComment('zelzis-random-product-slot');
    });

    // Replace only product-card slots. Other child nodes (including promo banners) stay in place.
    cards.forEach(function (card, index) {
      grid.replaceChild(slots[index], card);
    });
    slots.forEach(function (slot, index) {
      grid.replaceChild(shuffled[index], slot);
    });

    gridSignatures.set(grid, getSignature(shuffled));
    grid.setAttribute('data-zelzis-random-products', 'true');
    return true;
  }

  function collectGrids(root, includeProductBlocks) {
    var result = [];
    var seen = new Set();

    function add(grid) {
      if (!grid || seen.has(grid)) return;
      seen.add(grid);
      result.push(grid);
    }

    if (!root) root = document;
    if (root.nodeType === 1 && root.matches) {
      if (root.matches(COLLECTION_GRID_SELECTOR)) add(root);
      if (includeProductBlocks && root.matches(PRODUCT_BLOCK_GRID_SELECTOR)) add(root);
    }
    if (root.querySelectorAll) {
      Array.prototype.forEach.call(root.querySelectorAll(COLLECTION_GRID_SELECTOR), add);
      if (includeProductBlocks) {
        Array.prototype.forEach.call(root.querySelectorAll(PRODUCT_BLOCK_GRID_SELECTOR), add);
      }
    }
    return result;
  }

  function scanPage(force, includeProductBlocks) {
    collectGrids(document, Boolean(includeProductBlocks)).forEach(function (grid) {
      shuffleGrid(grid, Boolean(force));
    });
  }

  function scheduleScan(delay, force) {
    if (scanTimer) window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(function () {
      scanTimer = null;
      // Collection listings can be handled after DOMContentLoaded; homepage blocks wait until load.
      scanPage(Boolean(force), pageLoaded);
    }, typeof delay === 'number' ? delay : 300);
  }

  window.ZelzisRandomProducts = {
    shuffleWithin: function (root) {
      collectGrids(root, pageLoaded).forEach(function (grid) {
        shuffleGrid(grid, false);
      });
    },
    refresh: function () {
      scanPage(true, pageLoaded);
    }
  };

  // Collections are hidden by a tiny inline head script until this synchronous first shuffle.
  // Shuffling here prevents users from seeing the fixed server order and then watching it jump.
  if (document.body && document.body.classList.contains('template-collection')) {
    scanPage(false, false);
    document.documentElement.classList.remove('zelzis-random-pending');
  } else if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleScan(120, false);
    }, { once: true });
  } else {
    scheduleScan(120, false);
  }

  // Wait for the page's image/lazy-load/slider setup before handling homepage product blocks.
  window.addEventListener('load', function () {
    pageLoaded = true;
    scheduleScan(500, false);
  }, { once: true });

  // Support filter AJAX, infinite scroll, and product-block AJAX updates without racing slider setup.
  if ('MutationObserver' in window && document.documentElement) {
    var observer = new MutationObserver(function (mutations) {
      var relevantChange = mutations.some(function (mutation) {
        if (mutation.type !== 'childList') return false;
        var target = mutation.target;
        if (target.nodeType === 1 && target.closest &&
            target.closest('#CollectionProductGrid, #main-collection-product-grid, [data-product-block], [data-product-tab-block]')) {
          return true;
        }

        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && (
            node.matches(COLLECTION_GRID_SELECTOR) ||
            node.matches(PRODUCT_BLOCK_GRID_SELECTOR) ||
            Boolean(node.querySelector && node.querySelector(COLLECTION_GRID_SELECTOR + ', ' + PRODUCT_BLOCK_GRID_SELECTOR))
          );
        });
      });

      if (relevantChange) {
        var collectionGrid = document.querySelector(COLLECTION_GRID_SELECTOR);
        if (collectionGrid) {
          // MutationObserver callbacks run before the browser's next paint; shuffle appended/replaced
          // collection cards now, rather than exposing an unshuffled batch for a timer interval.
          shuffleGrid(collectionGrid, false);
        }
        scheduleScan(350, false);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // A page restored from the back/forward cache already has a visible old order.
  // Hide the collection grid and shuffle synchronously in the pageshow task before it paints.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;

    if (document.body && document.body.classList.contains('template-collection')) {
      document.documentElement.classList.add('zelzis-random-pending');
      scanPage(true, false);
      document.documentElement.classList.remove('zelzis-random-pending');
    } else {
      scheduleScan(120, true);
    }
  });
})();
